import React, {useState, useRef, useEffect} from 'react';
import t from 'babel-plugin-yate/macro';
import PropTypes from 'prop-types';
import cuid from 'cuid';

import {
  arrayBufferToBase64,
  determineOverSize,
  getFileExtensionFromMimeType,
  getMimeType,
  maxSizeNumber,
} from '../lib/misc';

import Icon from '../components/Icons/Checked';
import ValidationError from './Form/ValidationError';

export function FileUploadInput({
  accept,
  allowMultiple = false,
  id = cuid(),
  isFileUploaded,
  isFileMissing,
  isLabelUploadInputDisabled,
  label,
  onDone,
  triggerWarning,
  uploadedDocumentTypes,
  hideErrors = false,
  errorHandler = null,
}) {
  const acceptedFileTypes = accept.includes('image/*,.pdf')
    ? '.jpg, .jpeg, .png, .tif, .tiff, .webp, .pdf'
    : accept.includes('image/*')
      ? '.jpg, .jpeg, .png, .tif, .tiff, .webp'
      : accept;
  const humanReadableFileFormat =
    accept === 'image/*, .pdf'
      ? t`picture or .pdf`
      : accept === 'image/*'
        ? t`picture`
        : accept;
  const [isDirtyUpload, setIsDirtyUpload] = useState(false);
  const [isOverSize, setIsOverSize] = useState(false);
  const uploadInputRef = useRef();
  const handleChange = event => {
    event.persist();

    const allFiles = [];
    const rawData = Array.from(event.target.files);

    for (let file of rawData) {
      let isUnknownFileType = false;

      setIsOverSize(determineOverSize(file.size));

      if (determineOverSize(file.size)) {
        return;
      }

      const reader = new FileReader();

      reader.readAsArrayBuffer(file);

      reader.onloadend = function (e) {
        if (e.target.readyState === FileReader.DONE) {
          const bytes = [];
          // create a TypedArray
          const uint = new Uint8Array(e.target.result);

          // use only the first 4 bytes and transform them to hexadecimal values
          uint.slice(0, 4).forEach(byte => bytes.push(byte.toString(16)));

          const hex = bytes.join('').toUpperCase();

          isUnknownFileType =
            getMimeType(hex) === 'Unknown filetype' ||
            !acceptedFileTypes.includes(
              getFileExtensionFromMimeType(getMimeType(hex)),
            );

          setIsDirtyUpload(isUnknownFileType);

          if (isUnknownFileType) {
            uploadInputRef.current.value = '';

            return;
          }

          allFiles.push({
            base64: arrayBufferToBase64(reader.result),
            file,
            hex,
            id,
            name: file.name,
            size: Math.round(file.size / 1024) + ' kB',
            type: getMimeType(hex),
          });
        }

        if (allFiles.length === rawData.length) {
          /**
           * if the number of validated files is equal with
           * the number of uploaded files, do the API call
           * */

          if (onDone) {
            allowMultiple ? onDone(allFiles) : onDone(allFiles[0]);
          }
        }
      };
    }
  };

  useEffect(() => {
    if (errorHandler) {
      errorHandler(isDirtyUpload, isOverSize);
    }
  }, [errorHandler, isDirtyUpload, isOverSize]);

  const isUploaded =
    uploadedDocumentTypes && uploadedDocumentTypes.includes(id);

  return (
    <div className="pb-2">
      <input
        accept={accept}
        className={`w-full ${label ? 'hidden' : ''}`}
        id={id}
        multiple={allowMultiple}
        onClick={triggerWarning}
        onChange={handleChange}
        type="file"
        ref={uploadInputRef}
      />

      {label && (
        <label
          htmlFor={id}
          className={`flex ${
            label === t`Upload document`
              ? 'w-content py-3 px-6 bg-blue text-white rounded'
              : 'justify-between py-3 px-6'
          } items-center text-base font-medium ${isUploaded ? 'bg-grey-darker-4 ' : ''} ${
            isLabelUploadInputDisabled
              ? 'pointer-events-none'
              : 'cursor-pointer'
          }
          ${isLabelUploadInputDisabled ? 'opacity-50' : ''}
          `}>
          {label}
          {isUploaded && <Icon name="Checked" />}
        </label>
      )}

      {!hideErrors && isOverSize && (
        <ValidationError
          value={t`The maximum size of the uploaded document is [${maxSizeNumber}]MB. Please upload it after scanning it at a lower resolution.`}
        />
      )}

      {!hideErrors && isDirtyUpload && (
        <ValidationError
          value={t`Upload only ${humanReadableFileFormat} documents`}
        />
      )}

      {isFileMissing && <ValidationError value={t`This field is required`} />}

      {isFileUploaded && (
        <p className="mt-1 text-green text-12">{t`The document is loaded`}</p>
      )}
    </div>
  );
}

FileUploadInput.propTypes = {
  id: PropTypes.string,
  isFileUploaded: PropTypes.bool,
  isLabelUploadInputDisabled: PropTypes.bool,
  isFileMissing: PropTypes.bool,
  accept: PropTypes.string,
  allowMultiple: PropTypes.bool,
  onDone: PropTypes.func.isRequired,
  label: PropTypes.string,
  triggerWarning: PropTypes.func,
  uploadedDocumentTypes: PropTypes.array,
};
